import {useRef, useEffect, useCallback} from 'react';
import axios from 'axios';
import Qs from 'qs';

import Conf from 'utils/Conf';

const PLAYER_REFRESH_RATE = 15;

export function computeRanges(ranges, valueToAdd) {
  const length = ranges.length;
  for (let index = 0; index < length; index++) {
    const range = ranges[index];
    const [start, end] = range;
    const beforeElements = ranges.slice(0, index);
    const afterElements = ranges.slice(index + 1);

    // if value is significantly greater than end of current range
    // then jump to next iteration
    if (valueToAdd > end + PLAYER_REFRESH_RATE) {
      continue;
    }

    // if value is just greater than end of current range (not significantly)
    if (valueToAdd > end) {
      const nextRange = afterElements.shift() || [];
      const [nextStart, nextEnd] = nextRange;
      // if not last iteration and next range start if greater than value
      // then modify current range end
      if (nextStart && nextStart > valueToAdd) {
        return [...beforeElements, [start, valueToAdd], nextRange, ...afterElements];
      }

      // else modify current range end and remove next one
      return [...beforeElements, [start, Math.max(nextEnd || 0, valueToAdd)], ...afterElements];
    }

    // if value is between current range boundaries
    // then return unmodified ranges
    if (valueToAdd >= start) {
      return ranges;
    }

    // if value is just smaller than start of current range (not significantly)
    // then modify current range start
    // no need to check merge before (loop would have returned last iteration if it was the case)
    if (valueToAdd >= start - PLAYER_REFRESH_RATE) {
      return [...beforeElements, [valueToAdd, end], ...afterElements];
    }

    // if value is significantly smaller than start of current range
    // then insert new range before current one
    if (valueToAdd < start - PLAYER_REFRESH_RATE) {
      return [...beforeElements, [valueToAdd, valueToAdd], range, ...afterElements];
    }
  }
  // if iteration end without returning
  // then insert new range at the end
  return [...ranges, [valueToAdd, valueToAdd]];
}

const useViewingStats = seance => {
  const ranges = useRef(null);
  const timecode = useRef(0);

  useEffect(() => {
    if (ranges.current || !seance.ranges) return;
    ranges.current = seance.ranges.map(range => [parseFloat(range[0]), parseFloat(range[1])]);
  }, [seance.ranges]);

  useEffect(() => {
    const sendViewStats = () => {
      if (!timecode.current) return;

      const updatedRanges = computeRanges(ranges.current, timecode.current);
      const total = Math.round(updatedRanges.reduce((total, r) => total + r[1] - r[0], 0));
      ranges.current = updatedRanges;

      axios.post(
        `${Conf.apiBaseUriLegacy}/view_stats/${seance.seance_id}`,
        Qs.stringify({
          t: timecode.current,
          total_vu: total,
          ranges: updatedRanges,
          buffering_time: 0, // TODO: Add bufferingTime handler
        }),
        {
          withCredentials: true,
        },
      );
    };

    const viewStatsInterval = setInterval(sendViewStats, 10000);

    return () => {
      clearInterval(viewStatsInterval);
    };
  }, [seance.seance_id]);

  const updateStats = useCallback(newCurrentTimecode => {
    timecode.current = newCurrentTimecode;
  }, []);

  return updateStats;
};

export default useViewingStats;
